/**
 * Pantalla de comparación de flota.
 *
 * Recorre la flota por lotes, va mostrando el avance, y arma la tabla con
 * `core/fleet`, que es puro y está probado. Aquí no se calcula nada: se pinta.
 *
 * Lo que esta pantalla se niega a hacer es una sola tabla con todos los
 * vehículos ordenados por episodios. Sería la pantalla más fácil de leer y la
 * más fácil de leer mal.
 */

import { useCallback, useMemo, useState } from "react";
import {
  buildFleetComparison,
  type FleetVehicleRow,
  type ProfileGroup,
  type RankedVehicle,
} from "@core/fleet/fleet-comparison.ts";
import { requestFleetBatch } from "@web/lib/api-client.ts";
import { useBatchWalk, toBatchPage } from "@web/lib/batch-walk.ts";
import { Card, Notice } from "@web/components/primitives.tsx";

const number = (value: number | null, digits = 1) =>
  value === null ? "—" : value.toLocaleString("es-CO", { maximumFractionDigits: digits });

export function FleetPanel({ period }: { period: { fromIso: string; toIso: string } | null }) {
  const [minimumDistanceKm, setMinimumDistanceKm] = useState(20);

  const fetchPage = useCallback(
    async (input: { cursor: string | null; fromIso: string; toIso: string }) => {
      const response = await requestFleetBatch({
        fromIso: input.fromIso,
        toIso: input.toIso,
        cursor: input.cursor,
      });
      return toBatchPage<FleetVehicleRow>(response, response.rows);
    },
    [],
  );

  const walk = useBatchWalk<FleetVehicleRow>(fetchPage);

  const comparison = useMemo(
    () => (walk.items.length ? buildFleetComparison({ rows: walk.items, minimumDistanceKm }) : null),
    [walk.items, minimumDistanceKm],
  );

  return (
    <div className="space-y-4">
      <Card
        title="Comparar la flota"
        hint="Analiza todos los vehículos autorizados en el mismo período y los ordena por tasa por 100 km."
      >
        <div className="flex flex-wrap items-end gap-3">
          <button
            type="button"
            disabled={!period || walk.running}
            onClick={() => period && void walk.start(period)}
            className="rounded-md bg-brand-600 px-3 py-1.5 text-sm font-medium text-white disabled:opacity-40"
          >
            {walk.running ? "Recorriendo…" : "Comparar flota"}
          </button>

          {walk.running ? (
            <button
              type="button"
              onClick={walk.stop}
              className="rounded-md border border-zinc-300 px-3 py-1.5 text-sm dark:border-zinc-700"
            >
              Detener
            </button>
          ) : null}

          <label className="flex flex-col gap-1 text-xs text-zinc-600 dark:text-zinc-400">
            Distancia mínima para entrar al ranking
            <select
              className="rounded-md border border-zinc-300 bg-white px-2 py-1.5 text-sm text-zinc-900 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-100"
              value={minimumDistanceKm}
              onChange={(event) => setMinimumDistanceKm(Number(event.target.value))}
            >
              <option value={0}>Sin mínimo</option>
              <option value={20}>20 km</option>
              <option value={100}>100 km</option>
              <option value={500}>500 km</option>
            </select>
          </label>
        </div>

        {walk.batches > 0 ? (
          <div className="mt-4">
            <div className="flex justify-between text-xs text-zinc-500 dark:text-zinc-400">
              <span>
                {walk.processed} de {walk.total} vehículos
                {walk.failures.length ? ` · ${walk.failures.length} sin datos` : ""}
              </span>
              <span>{walk.complete ? "Completo" : walk.running ? "En curso" : "Detenido"}</span>
            </div>
            <div className="mt-1 h-1.5 overflow-hidden rounded bg-zinc-200 dark:bg-zinc-800">
              <div
                className="h-full bg-brand-600 transition-all"
                style={{ width: `${walk.total ? (walk.processed / walk.total) * 100 : 0}%` }}
              />
            </div>
          </div>
        ) : null}
      </Card>

      {walk.error ? <Notice title="El recorrido se interrumpió">{walk.error}</Notice> : null}

      {!walk.complete && !walk.running && walk.batches > 0 ? (
        <Notice tone="warning" title="La flota quedó a medias">
          Se recorrieron {walk.processed} de {walk.total} vehículos. La tabla de abajo describe solo a
          esos: no es la flota completa.
        </Notice>
      ) : null}

      {comparison?.warnings.map((warning) => (
        <Notice key={warning} tone="warning" title="Cómo hay que leer esta tabla">
          {warning}
        </Notice>
      ))}

      {comparison?.groups.map((group) => <GroupTable key={group.profileId} group={group} />)}

      {comparison && comparison.excluded.length ? (
        <Card
          title={`Fuera del ranking · ${comparison.excluded.length} vehículo(s)`}
          hint="No se ocultan: se explican. Un vehículo que desaparece en silencio es peor que un error."
        >
          <ul className="space-y-3">
            {comparison.excluded.map((vehicle) => (
              <li key={vehicle.deviceId} className="text-sm">
                <span className="font-medium text-zinc-900 dark:text-zinc-100">
                  {vehicle.plate ?? vehicle.name ?? vehicle.deviceId}
                </span>
                <p className="mt-0.5 text-xs leading-relaxed text-zinc-600 dark:text-zinc-400">
                  {vehicle.explanation}
                </p>
              </li>
            ))}
          </ul>
        </Card>
      ) : null}

      {walk.failures.length ? (
        <Card title={`Sin datos · ${walk.failures.length} vehículo(s)`} hint="La telemetría no entregó su historial.">
          <ul className="text-sm text-zinc-600 dark:text-zinc-400">
            {walk.failures.map((failure) => (
              <li key={failure.deviceId}>
                {failure.plate ?? failure.deviceId} — {failure.reason}
              </li>
            ))}
          </ul>
        </Card>
      ) : null}
    </div>
  );
}

function GroupTable({ group }: { group: ProfileGroup }) {
  return (
    <Card
      title={`Perfil ${group.profileId} · ${group.vehicles.length} vehículo(s)`}
      hint={group.comparability}
    >
      <p className="mb-3 text-xs text-zinc-500 dark:text-zinc-400">
        Tasa agregada del grupo:{" "}
        <span className="font-medium tabular-nums text-zinc-800 dark:text-zinc-200">
          {number(group.groupEpisodesPer100Km)} episodios / 100 km
        </span>{" "}
        sobre {number(group.groupDistanceKm, 0)} km recorridos.
      </p>

      <div className="overflow-x-auto">
        <table className="w-full min-w-[46rem] text-sm">
          <thead>
            <tr className="border-b border-zinc-200 text-left text-xs text-zinc-500 dark:border-zinc-800 dark:text-zinc-400">
              <th className="py-1.5 pr-2 font-medium">#</th>
              <th className="py-1.5 pr-2 font-medium">Vehículo</th>
              <th className="py-1.5 pr-2 text-right font-medium">km</th>
              <th className="py-1.5 pr-2 text-right font-medium">Episodios</th>
              <th className="py-1.5 pr-2 text-right font-medium">/100 km</th>
              <th className="py-1.5 pr-2 text-right font-medium">Acel.</th>
              <th className="py-1.5 pr-2 text-right font-medium">Frenada</th>
              <th className="py-1.5 pr-2 text-right font-medium">Giro</th>
              <th className="py-1.5 pr-2 text-right font-medium">Vel.</th>
              <th className="py-1.5 pr-2 text-right font-medium">Altos+crít.</th>
            </tr>
          </thead>
          <tbody>
            {group.vehicles.map((vehicle) => (
              <VehicleRow key={vehicle.deviceId} vehicle={vehicle} />
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

function VehicleRow({ vehicle }: { vehicle: RankedVehicle }) {
  const parked = vehicle.averageSpeedKmh !== null && vehicle.averageSpeedKmh < 5;
  return (
    <tr className="border-b border-zinc-100 last:border-0 dark:border-zinc-800/60">
      <td className="py-1.5 pr-2 tabular-nums text-zinc-400">{vehicle.rank}</td>
      <td className="py-1.5 pr-2">
        <span className="font-medium text-zinc-900 dark:text-zinc-100">
          {vehicle.plate ?? vehicle.name ?? vehicle.deviceId}
        </span>
        {vehicle.status === "PARTIAL" ? (
          <span className="ml-1.5 text-xs text-amber-700 dark:text-amber-500">parcial</span>
        ) : null}
        {parked ? (
          <span className="ml-1.5 text-xs text-amber-700 dark:text-amber-500">
            {number(vehicle.averageSpeedKmh)} km/h medios
          </span>
        ) : null}
      </td>
      <td className="py-1.5 pr-2 text-right tabular-nums">{number(vehicle.distanceKm, 0)}</td>
      <td className="py-1.5 pr-2 text-right tabular-nums">{vehicle.episodes.total}</td>
      <td className="py-1.5 pr-2 text-right font-medium tabular-nums">
        {number(vehicle.episodesPer100Km)}
      </td>
      <td className="py-1.5 pr-2 text-right tabular-nums text-zinc-500">
        {number(vehicle.accelerationPer100Km)}
      </td>
      <td className="py-1.5 pr-2 text-right tabular-nums text-zinc-500">
        {number(vehicle.brakingPer100Km)}
      </td>
      <td className="py-1.5 pr-2 text-right tabular-nums text-zinc-500">
        {number(vehicle.turnPer100Km)}
      </td>
      <td className="py-1.5 pr-2 text-right tabular-nums text-zinc-500">
        {number(vehicle.elevatedSpeedPer100Km)}
      </td>
      <td className="py-1.5 pr-2 text-right tabular-nums">
        {vehicle.severity.high + vehicle.severity.critical}
      </td>
    </tr>
  );
}